import React, { useState } from 'react';

// --- Type Definitions ---

type ReviewFormData = {
  rating: number;
  productName: string; // <--- NEW FIELD ADDED
  reviewTitle: string;
  reviewBody: string;
  reviewerName: string;
  reviewerEmail: string;
};

type GrievanceFormData = {
  fullName: string;
  emailId: string;
  phoneNumber: string;
  grievanceType: string;
  orderNumber: string;
  subject: string;
  description: string;
  incidentDate: string;
  attachments: FileList | null;
};

// --- Helper Components ---

/**
 * Star Rating Component
 */
const StarRating: React.FC<{ rating: number, setRating: (r: number) => void }> = ({ rating, setRating }) => {
  return (
    // Reduced star size (w-5 h-5)
    <div className="flex items-center space-x-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          onClick={() => setRating(star)}
          className={`w-5 h-5 cursor-pointer transition-colors duration-200 ${
            star <= rating ? 'text-yellow-500' : 'text-gray-300 hover:text-yellow-400'
          }`}
          fill="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M12 .587l3.668 7.568 8.332 1.207-6.001 5.869 1.416 8.307L12 18.896l-7.415 3.842 1.416-8.307-6.001-5.869 8.332-1.207L12 .587z" />
        </svg>
      ))}
    </div>
  );
};

/**
 * Compact Input Field Component (ULTRA-COMPACT: py-1, Fixed Width: max-w-sm)
 */
const CompactInput: React.FC<{ 
  name: keyof (ReviewFormData & GrievanceFormData); 
  placeholder: string; 
  required: boolean; 
  value: string; 
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
}> = (props) => (
  <input
    type={props.type || 'text'}
    name={props.name as string}
    required={props.required}
    value={props.value}
    onChange={props.onChange as any}
    placeholder={props.placeholder}
    // w-full max-w-sm: Ensures it takes full width on small screens, but is capped at max-w-sm on large screens.
    className="w-full max-w-sm px-3 py-1 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-red-500 focus:border-red-500 transition duration-150"
  />
);

/**
 * Compact Textarea Field Component (ULTRA-COMPACT: py-1, Full Width for description)
 */
const CompactTextarea: React.FC<{ 
  name: keyof (ReviewFormData & GrievanceFormData); 
  placeholder: string; 
  required: boolean; 
  value: string; 
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  rows: number;
}> = (props) => (
  <textarea
    name={props.name as string}
    required={props.required}
    value={props.value}
    onChange={props.onChange}
    placeholder={props.placeholder}
    rows={props.rows}
    // w-full (no max-width): Textareas should usually span the full column for descriptions.
    className="w-full px-3 py-1 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-red-500 focus:border-red-500 transition duration-150"
  />
);

/**
 * Compact Label Component (ULTRA-COMPACT: mb-0.5)
 */
const CompactLabel: React.FC<{ htmlFor: string, text: string, required: boolean }> = ({ htmlFor, text, required }) => (
    <label htmlFor={htmlFor} className="block text-sm font-medium text-gray-700 mb-0.5">
      {text} {required && <span className="text-red-500">*</span>}
    </label>
);


// --- Main Combined Component ---

const CombinedFeedbackPage: React.FC = () => {
  // State for Review Form
  const [reviewFormData, setReviewFormData] = useState<ReviewFormData>({
    rating: 0,
    productName: '', // <-- NEW FIELD INITIALIZATION
    reviewTitle: '',
    reviewBody: '',
    reviewerName: '',
    reviewerEmail: '',
  });

  // State for Grievance Form
  const [grievanceFormData, setGrievanceFormData] = useState<GrievanceFormData>({
    fullName: '',
    emailId: '',
    phoneNumber: '',
    grievanceType: '',
    orderNumber: '',
    subject: '',
    description: '',
    incidentDate: new Date().toISOString().substring(0, 10),
    attachments: null,
  });

  // Handlers
  const handleReviewChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setReviewFormData({
      ...reviewFormData,
      [e.target.name]: e.target.value,
    });
  };

  const handleGrievanceChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setGrievanceFormData({
      ...grievanceFormData,
      [e.target.name]: e.target.value,
    });
  };

  const handleReviewRating = (newRating: number) => {
    setReviewFormData({ ...reviewFormData, rating: newRating });
  };

  const handleGrievanceFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGrievanceFormData({
      ...grievanceFormData,
      attachments: e.target.files,
    });
  };

  const handleReviewSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Review Submitted:', reviewFormData);
    alert('Review Submitted! (Check console for data)');
  };
  
  const handleGrievanceSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Grievance Submitted:', grievanceFormData);
    alert('Grievance Submitted! (Check console for data)');
  };


  return (
    <div className="bg-gray-50 min-h-screen">
      
      {/* Header Banner */}
      <div className="bg-blue-950 text-white py-10 px-4 sm:px-6 lg:px-8 shadow-2xl">
          <div className="max-w-6xl mx-auto text-center">
              <h1 className="text-4xl font-extrabold mb-2 text-yellow-300">FEEDBACK & Write Your Review</h1>
              <p className="text-lg text-gray-300">Submit a product review or write to our management team for prompt resolution of any issues.</p>
          </div>
      </div>

      <div className="max-w-8xl mx-auto p-4 sm:p-6 lg:p-8">
        
        {/* --- 1. Write a Review Section (ULTRA-COMPACT with fixed input width) --- */}
        <div className="bg-white rounded-xl shadow-xl p-6 sm:p-8 mb-10 border-t-4 border-red-600">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-3">
             <span className="text-red-600 mr-2">⭐</span> Submit Product Review
          </h2>

          <form onSubmit={handleReviewSubmit} className="space-y-4">
            
            {/* Rating */}
            <div className="mb-4">
              <CompactLabel htmlFor="rating" text="Overall Rating" required={true} />
              <div className="flex items-center space-x-4">
                  <StarRating rating={reviewFormData.rating} setRating={handleReviewRating} />
                  {reviewFormData.rating > 0 && (
                      <p className="text-sm font-medium text-green-600">
                         {reviewFormData.rating} / 5
                      </p>
                  )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* NEW FIELD: Product/Service Name */}
                <div>
                    <CompactLabel htmlFor="productName" text="Product/Service Name" required={true} />
                    <CompactInput name="productName" placeholder="E.g., Hammer Drill XYZ, Delivery Service" required={true} value={reviewFormData.productName} onChange={handleReviewChange} />
                </div>
                {/* Review Title */}
                <div>
                    <CompactLabel htmlFor="reviewTitle" text="Review Title" required={true} />
                    <CompactInput name="reviewTitle" placeholder="Brief and catchy title" required={true} value={reviewFormData.reviewTitle} onChange={handleReviewChange} />
                </div>
                {/* Reviewer Name */}
                <div>
                    <CompactLabel htmlFor="reviewerName" text="Your Name" required={true} />
                    <CompactInput name="reviewerName" placeholder="Enter your full name" required={true} value={reviewFormData.reviewerName} onChange={handleReviewChange} />
                </div>
                {/* Reviewer Email (Fixed width due to CompactInput's max-w-sm) */}
                <div> {/* Changed from md:col-span-2 to simple div */}
                    <CompactLabel htmlFor="reviewerEmail" text="Email ID" required={true} />
                    <CompactInput name="reviewerEmail" placeholder="Enter your email address" required={true} value={reviewFormData.reviewerEmail} onChange={handleReviewChange} type="email" />
                </div>
            </div>

            {/* Detailed Review Body - Full width for description */}
            <div>
                <CompactLabel htmlFor="reviewBody" text="Detailed Review" required={true} />
                <CompactTextarea name="reviewBody" placeholder="Share your detailed experience (min 50 characters)" required={true} value={reviewFormData.reviewBody} onChange={handleReviewChange as any} rows={2} />
            </div>

            {/* Submit Button (ULTRA-COMPACT: py-1 px-4) */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={reviewFormData.rating === 0}
                className={`w-64 py-1 px-4 rounded-md text-base font-bold transition-all duration-300 ${
                  reviewFormData.rating === 0 
                    ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
                    : 'bg-red-600 text-white hover:bg-red-700 shadow-md'
                }`}
              >
                SUBMIT REVIEW
              </button>
            </div>
          </form>
        </div>

        {/* --- 2. Write to Management (Grievance Form) Section (Fixed input width applied) --- */}
        <div className="bg-white rounded-xl shadow-xl p-6 sm:p-8 border-t-4 border-blue-600">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-3">
             <span className="text-blue-600 mr-2">✍️</span> Write to Management (Grievance)
          </h2>
          
          <form onSubmit={handleGrievanceSubmit} className="space-y-4">

            {/* Personal Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Personal Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div><CompactLabel htmlFor="fullName" text="Full Name" required={true} /><CompactInput name="fullName" placeholder="Enter your full name" required={true} value={grievanceFormData.fullName} onChange={handleGrievanceChange as any} /></div>
                <div><CompactLabel htmlFor="emailId" text="Email ID" required={true} /><CompactInput name="emailId" placeholder="Enter your email address" required={true} value={grievanceFormData.emailId} onChange={handleGrievanceChange as any} type="email" /></div>
                <div><CompactLabel htmlFor="phoneNumber" text="Phone Number" required={true} /><CompactInput name="phoneNumber" placeholder="Enter 10-digit mobile number" required={true} value={grievanceFormData.phoneNumber} onChange={handleGrievanceChange as any} type="tel" /></div>
              </div>
            </div>

            {/* Grievance Details */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Grievance Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                
                {/* Grievance Type (Select) */}
                <div>
                  <CompactLabel htmlFor="grievanceType" text="Grievance Type" required={true} />
                  <select
                    name="grievanceType"
                    required
                    value={grievanceFormData.grievanceType}
                    onChange={handleGrievanceChange}
                    // Added max-w-sm here too
                    className="w-full max-w-sm px-3 py-1 border border-gray-300 bg-white rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500 transition duration-150"
                  >
                    <option value="">Select Type</option>
                    <option value="Product Issue">Product Issue</option>
                    <option value="Delivery Delay">Delivery Delay</option>
                    <option value="Billing Error">Billing Error</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                
                <div><CompactLabel htmlFor="orderNumber" text="Order Number (If Applicable)" required={false} /><CompactInput name="orderNumber" placeholder="Enter order number" required={false} value={grievanceFormData.orderNumber} onChange={handleGrievanceChange as any} /></div>
                <div><CompactLabel htmlFor="subject" text="Subject" required={true} /><CompactInput name="subject" placeholder="Brief subject of your grievance" required={true} value={grievanceFormData.subject} onChange={handleGrievanceChange as any} /></div>
              </div>
            </div>

            {/* Additional Information (Description, Date, Attachment) */}
            <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Additional Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    
                    {/* Detailed Description (2/3 width) - Full width in its column */}
                    <div className="md:col-span-2">
                        <CompactLabel htmlFor="description" text="Detailed Description" required={true} />
                        <CompactTextarea name="description" placeholder="Provide a detailed description of your grievance (50-2000 chars)" required={true} value={grievanceFormData.description} onChange={handleGrievanceChange as any} rows={2} />
                    </div>
                    
                    {/* Date and Attachments (1/3 width, stacked) */}
                    <div className="space-y-4">
                        <div>
                            <CompactLabel htmlFor="incidentDate" text="Incident Date (If Applicable)" required={false} />
                            <CompactInput name="incidentDate" placeholder="" required={false} value={grievanceFormData.incidentDate} onChange={handleGrievanceChange as any} type="date" />
                        </div>
                        
                        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm mb-4">
  <h3 className="text-sm font-semibold text-gray-700 mb-2">
    Attachments <span className="text-gray-400 text-xs">(PDF, JPG, PNG)</span>
  </h3>

  <label className="block cursor-pointer">
    <input
      type="file"
      name="attachments"
      id="attachments"
      onChange={handleGrievanceFileChange}
      accept=".pdf,.jpg,.jpeg,.png"
      className="block w-full text-sm text-gray-600 border border-gray-300 rounded-md cursor-pointer
        file:mr-4 file:py-1 file:px-3
        file:rounded-md file:border-0
        file:text-sm file:font-semibold
        file:bg-blue-50 file:text-blue-700
        hover:file:bg-blue-100
        focus:outline-none"
    />
  </label>

  <p className="text-xs text-gray-400 mt-1">
    You can upload supporting documents or screenshots if needed.
  </p>
</div>

                    </div>
                </div>
            </div>

            {/* Submit Button (ULTRA-COMPACT: py-1 px-4) */}
            <div className="flex justify-end pt-4">
              <button
                type="submit"
                className="py-1 px-4 rounded-md text-base font-bold bg-blue-600 text-white hover:bg-blue-700 transition duration-150 shadow-md"
              >
                SUBMIT GRIEVANCE
              </button>
            </div>
          </form>
        </div>
        
      </div>
    </div>
  );
};

export default CombinedFeedbackPage;